import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import styles from "@/styles/components/compliance-center/ComplianceCenterNavTabs.module.scss";
import entStyles from "@/styles/enterprise/EnterpriseOperationalUx.module.scss";

export type ComplianceCenterNavTabItem = {
  key: string;
  to: string;
  label: string;
  icon?: ReactNode;
};

export type ComplianceCenterNavSection = {
  key: string;
  label: string;
  items: ComplianceCenterNavTabItem[];
};

export interface ComplianceCenterNavTabsProps {
  items?: ComplianceCenterNavTabItem[];
  sections?: ComplianceCenterNavSection[];
  /** Enterprise operational UX: posture panel + outline nav chips */
  variant?: "default" | "enterprise";
  ariaLabel?: string;
}

function tabIsActive(pathname: string, to: string): boolean {
  return pathname === to || pathname.startsWith(`${to}/`);
}

function resolveNavEntries(props: ComplianceCenterNavTabsProps): ComplianceCenterNavTabItem[] {
  if (props.sections?.length) {
    return props.sections.flatMap((section) => section.items);
  }
  return props.items || [];
}

export function ComplianceCenterNavTabs({
  items,
  sections,
  variant = "default",
  ariaLabel = "Compliance Center sections",
}: ComplianceCenterNavTabsProps) {
  const { pathname } = useLocation();
  const enterprise = variant === "enterprise";
  const flatItems = resolveNavEntries({ items, sections });

  if (enterprise) {
    const groups = sections?.length ? sections : [{ key: "default", label: "", items: flatItems }];
    return (
      <nav className={entStyles.workspaceNavPanel} aria-label={ariaLabel}>
        {groups.map((section) => (
          <div key={section.key} className={entStyles.workspaceNavSection}>
            {section.label ? <p className={entStyles.workspaceNavSectionLabel}>{section.label}</p> : null}
            <div className={entStyles.workspaceNavTrack}>
              {section.items.map((item) => {
                const active = tabIsActive(pathname, item.to);
                return (
                  <NavLink
                    key={item.key}
                    to={item.to}
                    className={`${entStyles.workspaceNavTab} ${active ? entStyles.workspaceNavTabActive : ""}`.trim()}
                  >
                    {item.icon ? <span className={entStyles.workspaceNavTabIcon}>{item.icon}</span> : null}
                    <span>{item.label}</span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
    );
  }

  return (
    <nav className={styles.root} aria-label={ariaLabel}>
      <div className={styles.track}>
        {flatItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            className={() => `${styles.tab} ${tabIsActive(pathname, item.to) ? styles.tabActive : ""}`.trim()}
          >
            {item.icon ? <span className={styles.tabIcon}>{item.icon}</span> : null}
            <span className={styles.tabLabel}>{item.label}</span>
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

